/**
 * Utilitário para gerenciamento de autenticação e permissões (Vue 2 compatible)
 *
 * Centraliza a lógica de acesso ao localStorage e verificação de permissões,
 * evitando acesso direto ao localStorage espalhado pelos componentes.
 */

import { USER_ROLES, PERMISSION_GROUPS, hasRole, getRoleName, isSchemaAutonomo } from '@/constants/userRoles'

/**
 * Classe para gerenciamento de autenticação (compatível com Vue 2)
 */
class AuthManager {
  constructor () {
    this._user = null
  }

  // Carrega dados do usuário do localStorage
  loadUser () {
    try {
      const userData = localStorage.getItem('usuario')
      if (userData) {
        this._user = JSON.parse(userData)
      }
      return this._user
    } catch (error) {
      console.error('Erro ao carregar dados do usuário:', error)
      return null
    }
  }

  // Getter para usuário atual
  get currentUser () {
    if (!this._user) {
      this.loadUser()
    }
    return this._user
  }

  // Getter para role do usuário
  get userRole () {
    const userData = this.currentUser
    if (!userData) return null
    return userData.id_funcao_usuario || userData.id_role || null
  }

  // Getter para nome da role
  get userRoleName () {
    return getRoleName(this.userRole)
  }

  // Getter para status de autenticação
  get isAuthenticated () {
    return !!this.currentUser
  }

  // Métodos de verificação de permissões
  hasPermission (allowedRoles) {
    return hasRole(this.userRole, allowedRoles)
  }

  get canAccessTelegram () {
    return this.hasPermission(PERMISSION_GROUPS.TELEGRAM_ACCESS)
  }

  get canSendNotifications () {
    return this.hasPermission(PERMISSION_GROUPS.NOTIFICATION_SENDERS)
  }

  get canAccessAdminConfig () {
    return this.hasPermission(PERMISSION_GROUPS.ADMIN_ACCESS)
  }

  get canAccessMonitoring () {
    return this.hasPermission(PERMISSION_GROUPS.MONITORING_ACCESS)
  }

  // Tenant de instrutor autônomo (schema autonomo_<cpf>)
  get isAutonomo () {
    return isSchemaAutonomo(localStorage.getItem('schema'))
  }

  get canUseIntegracaoDetran () {
    // id_role direto do localStorage (como no router): o usuário em cache pode ser de um login anterior
    return this.isAutonomo && hasRole(localStorage.getItem('id_role'), PERMISSION_GROUPS.INTEGRACAO_DETRAN_AUTONOMO)
  }

  // Monitoramento e suporte associam veículos no Detran para qualquer autônomo, informando o schema
  get canAssociarVeiculosAutonomos () {
    return hasRole(localStorage.getItem('id_role'), PERMISSION_GROUPS.ASSOCIAR_VEICULOS_AUTONOMOS)
  }

  // Verificações específicas por role
  get isAdmin () {
    return this.hasPermission(USER_ROLES.ADMIN)
  }

  get isMonitor () {
    return this.hasPermission(USER_ROLES.MONITOR)
  }

  get isFinanceiro () {
    return this.hasPermission(USER_ROLES.FINANCEIRO)
  }

  get isAluno () {
    return this.hasPermission(USER_ROLES.ALUNO)
  }

  get isComercial () {
    return this.hasPermission(USER_ROLES.COMERCIAL)
  }

  get isDetran () {
    return this.hasPermission(USER_ROLES.DETRAN)
  }

  // Outros dados do usuário
  get userEmail () {
    const userData = this.currentUser
    return (userData && userData.email) || ''
  }

  get userName () {
    const userData = this.currentUser
    return (userData && (userData.name || userData.nome)) || ''
  }

  get userCfc () {
    const userData = this.currentUser
    return (userData && userData.cfc) || localStorage.getItem('cfc') || null
  }

  get userPermissions () {
    try {
      const permissions = localStorage.getItem('permissions')
      return permissions ? JSON.parse(permissions) : {}
    } catch (error) {
      console.error('Erro ao carregar permissões:', error)
      return {}
    }
  }

  // Método para atualizar dados do usuário
  updateUser (newUserData) {
    this._user = Object.assign({}, this._user, newUserData)
    localStorage.setItem('usuario', JSON.stringify(this._user))
  }

  // Método para fazer logout
  logout () {
    this._user = null
    localStorage.removeItem('usuario')
    localStorage.removeItem('permissions')
    localStorage.removeItem('cfc')
  }
}

// Instância singleton
const authManager = new AuthManager()

/**
 * Função compatível com Vue 2 que retorna o gerenciador de autenticação
 */
export function useAuth () {
  return authManager
}
